import { useState, useEffect } from 'react';
import type { EngineId } from '@geosoft/viewer-sdk';
import { Drawer, Icon, Stack, Text, tokens } from '@/design';
import { CompareScenariosPanel } from '@/components/compare/CompareScenariosPanel';
import { ShareViewPanel } from '@/components/share/ShareViewPanel';
import { ShareModal } from '@/components/share/ShareModal';
import { AuditDrawer } from '@/components/audit/AuditDrawer';
import {
  AppLauncherPanel,
  HelpPanel,
  NotificationsPanel,
  SearchAndCatalogPanel,
  UserSettingsPanel,
} from '@/components/support/SupportPanels';
import type { ShellDrawerId } from './ShellHeader';

interface ShellDrawersProps {
  activeDrawer: ShellDrawerId | null;
  onClose(): void;
  engine: EngineId;
}

const DRAWER_TITLES: Record<ShellDrawerId, string> = {
  'app-launcher': 'Application launcher',
  search: 'Global search',
  notifications: 'Notifications',
  help: 'Help',
  'user-menu': 'Account menu',
  compare: 'Compare scenarios',
  share: 'Share view',
  audit: 'Session audit',
};

const DRAWER_DESCRIPTIONS: Record<ShellDrawerId, string> = {
  'app-launcher': 'Consumed shell slot placeholder for GVW-006.',
  search: 'Consumed shell slot placeholder for GVW-009 and GVW-010.',
  notifications: 'Consumed shell slot placeholder for GVW-011 and GVW-012.',
  help: 'Consumed shell slot placeholder for GVW-015.',
  'user-menu': 'Consumed shell slot placeholder for GVW-013 and GVW-017.',
  compare: 'App-owned shell action placeholder for GVW-024 compare flow.',
  share: 'App-owned shell action placeholder for GVW-024 share flow.',
  audit: 'Session audit drawer showing user actions in the current session.',
};

export function ShellDrawers({ activeDrawer, onClose, engine }: ShellDrawersProps) {
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Open share modal when share drawer is activated
  useEffect(() => {
    if (activeDrawer === 'share') {
      setShareModalOpen(true);
      onClose(); // Close the drawer since we're opening a modal instead
    }
  }, [activeDrawer, onClose]);

  const handleCloseShareModal = () => {
    setShareModalOpen(false);
  };

  if (!activeDrawer) return (
    <>
      <ShareModal 
        isOpen={shareModalOpen} 
        onClose={handleCloseShareModal} 
        engine={engine} 
      />
    </>
  );

  const body =
    activeDrawer === 'compare' ? (
      <CompareScenariosPanel />
    ) : activeDrawer === 'share' ? (
      <ShareViewPanel engine={engine} />
    ) : activeDrawer === 'audit' ? (
      <AuditDrawer />
    ) : activeDrawer === 'notifications' ? (
      <NotificationsPanel />
    ) : activeDrawer === 'help' ? (
      <HelpPanel />
    ) : activeDrawer === 'user-menu' ? (
      <UserSettingsPanel />
    ) : activeDrawer === 'app-launcher' ? (
      <AppLauncherPanel />
    ) : activeDrawer === 'search' ? (
      <SearchAndCatalogPanel />
    ) : (
      <>
        <Stack direction="row" align="center" gap={1}>
          <Icon name="info" size={20} decorative />
          <Text variant="body" as="p" style={{ margin: 0 }}>
            {DRAWER_DESCRIPTIONS[activeDrawer]}
          </Text>
        </Stack>
        <Text variant="caption" as="p" style={{ margin: 0, color: tokens.color.ink.secondary }}>
          Drawer host integration is active and non-stacking by design.
        </Text>
      </>
    );

  return (
    <>
      <Drawer
        id={activeDrawer}
        title={DRAWER_TITLES[activeDrawer]}
        onClose={onClose}
        width={tokens.layout.rightPanel}
        data-testid="shell-active-drawer"
      >
        <Stack direction="column" gap={2}>
          {body}
        </Stack>
      </Drawer>
      
      <ShareModal 
        isOpen={shareModalOpen} 
        onClose={handleCloseShareModal} 
        engine={engine} 
      />
    </>
  );
}
